// eslint-disable-next-line no-restricted-imports
import type { DefaultGrammar, DeepPick as TsDeepPick, DeepPickPath as TsDeepPickPath } from 'ts-deep-pick';
import { pick } from 'lodash';

// For our use cases we only want to use the `prop` grammer
type DeepPickGrammar = DefaultGrammar & {
    array: never;
    glob: never;
    mutate: never;
    omit: never;
};

// Additional util to prevent circular deep picks
type OmitCircularAndDate<T, CurrentDepth extends number[] = []> = T extends object
    ? CurrentDepth['length'] extends 5
        ? never
        : T extends Date
          ? Omit<T, keyof Date>
          : { [K in keyof T]: OmitCircularAndDate<T[K], [...CurrentDepth, 1]> }
    : T;

export type DeepPickPath<T> = TsDeepPickPath<OmitCircularAndDate<T>, DeepPickGrammar>;
export type DeepPick<T, K extends TsDeepPickPath<OmitCircularAndDate<T>, DeepPickGrammar>> = TsDeepPick<
    OmitCircularAndDate<T>,
    K,
    DeepPickGrammar
>;

/**
 * The function `deepPick` wraps the Lodash function `pick` and alters the typing to support keyof deep paths.
 *
 * @param object see Lodash documentation
 * @param paths see Lodash documentation
 */
export function deepPick<
    Object extends Record<string, unknown>,
    Path extends DeepPickPath<Object> = DeepPickPath<Object>,
>(object: Object, paths: Path | Path[]): DeepPick<Object, Path> {
    return pick(object, paths) as DeepPick<Object, Path>;
}
